import { Link, Typography } from '@mui/material'
import { Icons } from 'utoniq-ui'

import {
  ConsolePreSaleEditFragment,
  Maybe,
  MembershipSubscriptionPlan,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  FirstComeFirstServedEditForm,
  FirstComeFirstServedEditValues,
} from '@/components/pages/ConsolePreSaleEdit/template/FirstComeFirstServedEditForm'
import {
  LotteryEditForm,
  LotteryEditValues,
} from '@/components/pages/ConsolePreSaleEdit/template/LotteryEditForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  artistId: string
  preSale: ConsolePreSaleEditFragment | null
  plans: Maybe<
    Pick<MembershipSubscriptionPlan, 'planId' | 'title' | 'interval'>
  >[]
  submitting: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: (
    values: LotteryEditValues | FirstComeFirstServedEditValues
  ) => Promise<void>
}

export const ConsolePreSaleEditTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { t } = useTranslation('consolePreSaleEdit')
  const { routerPages, generatePath } = useRouter<{ preSaleId: string }>()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <ConsoleBaseLayout
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ConsolePreSaleDetail, {
            preSaleId: state.preSale?.id ?? '#',
          }),
        }}
        margin
      >
        {state.preSale && (
          <>
            <Typography variant="h3" sx={{ py: 3, mt: 5 }}>
              {t(`${state.preSale.type}Title`)}
            </Typography>
            <Link
              component="a"
              target="_blank"
              href={getHelpUrl('preSale')}
              variant="caption"
              sx={{ alignItems: 'center', display: 'flex' }}
            >
              {t(`${state.preSale.type}Help`)}
              <Icons.Help fontSize="xSmall" color="inherit" sx={{ ml: 1 }} />
            </Link>
            {state.preSale.type === PreSaleType.Lottery ? (
              <LotteryEditForm
                onSubmit={onSubmit}
                preSale={state.preSale}
                submitting={state.submitting}
                plans={state.plans}
              />
            ) : (
              <FirstComeFirstServedEditForm
                onSubmit={onSubmit}
                preSale={state.preSale}
                submitting={state.submitting}
                plans={state.plans}
              />
            )}
          </>
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
